import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Badge from '../../components/ui/Badge';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import {
  CheckCircleIcon,
  ClockIcon,
  CogIcon,
  DocumentTextIcon,
  EyeIcon,
  GeneralList,
  GeneralListSortableTh,
  SegmentedControl,
  TrashIcon,
  UploadCloudIcon,
  UserPlusIcon,
  ViewColumnsIcon,
  useGeneralListSort,
} from '../../components/ui';
import { formatDateTime } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { deleteApproval } from '../../services/mutations';
import { Approval, ApprovalStatus, useApprovals } from './useData';
import { approvalSourceLabel, approvalSourcePath } from './sourceLinks';

const statusVariant: Record<ApprovalStatus, 'default' | 'info' | 'success' | 'danger' | 'warning'> = {
  DRAFT: 'default',
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'info',
};

const statusLabel: Record<ApprovalStatus, string> = {
  DRAFT: 'Draft',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

const statusAtCaption: Record<ApprovalStatus, string> = {
  DRAFT: 'Draft since',
  PENDING: 'Pending since',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

const departmentName = (approval: Approval) =>
  approval.department?.name || approval.department?.departmentName || '';

const displayUser = (
  user?: {
    userDisplayName?: string | null;
    userFirstName?: string;
    userLastName?: string;
  } | null,
) => {
  if (!user) return null;
  return (
    user.userDisplayName ||
    [user.userFirstName, user.userLastName].filter(Boolean).join(' ').trim() ||
    null
  );
};

type ApprovalAnalytics = {
  statusAt: string | null;
  submittedAt: string | null;
  createdAt: string;
  submittedBy: string | null;
  progressDone: number;
  progressTotal: number;
  currentStage: number | null;
  stageCount: number;
  lastActedAt: string | null;
  lastActor: string | null;
  lastComment: string | null;
  overdue: boolean;
  pendingAssignees: string[];
};

function approvalAnalytics(approval: Approval): ApprovalAnalytics {
  const steps = approval.steps ?? [];
  const finished = steps.filter((s) => s.status !== 'PENDING');
  const pending = steps.filter((s) => s.status === 'PENDING');
  const acted = [...steps]
    .filter((s) => s.actedAt)
    .sort((a, b) => new Date(a.actedAt!).getTime() - new Date(b.actedAt!).getTime());
  const latest = acted[acted.length - 1];
  const stages = new Set(steps.map((s) => s.stage));
  const overdue = pending.some((s) => s.dueAt && new Date(s.dueAt).getTime() < Date.now());
  const pendingAssignees = Array.from(
    new Set(
      pending
        .map(
          (s) =>
            displayUser(s.assigneeUser) ||
            s.assigneeDepartment?.name ||
            s.assigneeDepartment?.departmentName ||
            s.assigneeRank?.name ||
            null,
        )
        .filter((n): n is string => !!n),
    ),
  );

  return {
    statusAt: approval.statusAt || approval.updatedAt || approval.createdAt || null,
    submittedAt: approval.submittedAt || null,
    createdAt: approval.createdAt,
    submittedBy: displayUser(approval.createdByUser),
    progressDone: finished.length,
    progressTotal: steps.length,
    currentStage: pending.length ? Math.min(...pending.map((s) => s.stage)) : null,
    stageCount: stages.size,
    lastActedAt: latest?.actedAt || null,
    lastActor: displayUser(latest?.actedByUser),
    lastComment: latest?.comments?.trim() || null,
    overdue,
    pendingAssignees,
  };
}

type InboxTab = 'mine' | 'submitted' | 'all';

const tabs: Array<{
  id: InboxTab;
  label: string;
  icon: React.ReactNode;
  emptyTitle: string;
  emptyHint: string;
}> = [
  {
    id: 'mine',
    label: 'My pending',
    icon: <ClockIcon className="h-4 w-4" />,
    emptyTitle: 'Nothing waiting on you',
    emptyHint:
      'When someone submits a request assigned to you — now or on a later stage — it will show up here.',
  },
  {
    id: 'submitted',
    label: 'Submitted by me',
    icon: <UploadCloudIcon className="h-4 w-4" />,
    emptyTitle: 'No submissions yet',
    emptyHint: 'Requests you submit for approval from any module will appear here.',
  },
  {
    id: 'all',
    label: 'All',
    icon: <ViewColumnsIcon className="h-4 w-4" />,
    emptyTitle: 'No approvals found',
    emptyHint: 'Try another tab or clear search. Approvals can come from documents and other modules.',
  },
];

const statusFilterOptions: Array<{ id: string; label: string }> = [
  { id: '', label: 'All statuses' },
  { id: 'PENDING', label: 'Pending' },
  { id: 'APPROVED', label: 'Approved' },
  { id: 'REJECTED', label: 'Rejected' },
  { id: 'DRAFT', label: 'Draft' },
  { id: 'CANCELLED', label: 'Cancelled' },
];

export const List: React.FC = () => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = { from: `${location.pathname}${location.search}` };
  const [inbox, setInbox] = useState<InboxTab>('mine');
  const [statusFilter, setStatusFilter] = useState<'' | ApprovalStatus>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'statusAt', direction: 'desc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'title' ||
        key === 'sourceModule' ||
        key === 'approvalType' ||
        key === 'status' ||
        key === 'department'
          ? 'asc'
          : 'desc',
    },
  );
  const statusQuery = inbox === 'mine' ? undefined : statusFilter || undefined;
  const { data, isLoading, mutate } = useApprovals(inbox, 1, search, {
    status: statusQuery,
    sortKey: sortConfig.key,
    sortDirection: sortConfig.direction,
  });
  const activeTab = useMemo(() => tabs.find((t) => t.id === inbox) ?? tabs[0], [inbox]);
  const [pendingDelete, setPendingDelete] = useState<Approval | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const grouped = useMemo(() => {
    const order: ApprovalStatus[] = ['PENDING', 'DRAFT', 'APPROVED', 'REJECTED', 'CANCELLED'];
    const map = new Map<ApprovalStatus, Approval[]>();
    for (const status of order) map.set(status, []);
    for (const approval of data) {
      const list = map.get(approval.status) ?? [];
      list.push(approval);
      map.set(approval.status, list);
    }
    return order
      .map((status) => ({ status, items: map.get(status) ?? [] }))
      .filter((g) => g.items.length > 0);
  }, [data]);

  const defaultSort =
    sortConfig.key === 'statusAt' && sortConfig.direction === 'desc';
  const showGrouped = inbox !== 'mine' && !statusFilter && defaultSort;
  const hasFilters = Boolean(search.trim() || (inbox !== 'mine' && statusFilter));

  const switchInbox = (next: InboxTab) => {
    setInbox(next);
    if (next === 'mine') setStatusFilter('');
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
  };

  const tableHeader = (
    <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
      <th className="w-[5.5rem] px-3 py-3 font-medium" />
      <GeneralListSortableTh sortKey="title">Title</GeneralListSortableTh>
      <GeneralListSortableTh sortKey="sourceModule" className="hidden sm:table-cell">
        Source
      </GeneralListSortableTh>
      <GeneralListSortableTh sortKey="approvalType" className="hidden lg:table-cell">
        Type
      </GeneralListSortableTh>
      <GeneralListSortableTh sortKey="status" className="w-44">
        Status
      </GeneralListSortableTh>
      <GeneralListSortableTh sortKey="submittedAt" className="hidden w-36 md:table-cell">
        Submitted
      </GeneralListSortableTh>
      <th className={cn('hidden w-28 font-medium xl:table-cell')}>Progress</th>
      <th className={cn('hidden w-44 font-medium 2xl:table-cell')}>Last action</th>
      <GeneralListSortableTh sortKey="department" className="hidden xl:table-cell">
        Department
      </GeneralListSortableTh>
    </tr>
  );

  const renderRows = (items: Approval[]) =>
    items.map((approval) => (
      <ApprovalRow
        key={approval.id}
        approval={approval}
        colors={colors}
        canDelete={!!user && approval.createdByUserId === user.id}
        returnState={returnState}
        onOpen={() => navigate(`/approvals/${approval.id}`, { state: returnState })}
        onDelete={() => {
          setDeleteError(null);
          setPendingDelete(approval);
        }}
      />
    ));

  return (
    <>
      <GeneralList
        tabs={tabs.map(({ id, label, icon }) => ({ id, label, icon }))}
        activeTab={inbox}
        onTabChange={switchInbox}
        sorting={{ sortConfig, onSort: handleSort }}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: 'Search approvals…',
        }}
        actions={
          <>
            <Link
              to="/approvals/delegations"
              state={returnState}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-sm font-medium transition hover:bg-black/[0.03] dark:hover:bg-white/[0.04]',
                colors.border,
                colors.text,
              )}
            >
              <UserPlusIcon className="h-4 w-4 opacity-70" />
              Delegations
            </Link>
            <Link
              to="/approvals/templates"
              state={returnState}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-sm font-medium transition hover:bg-black/[0.03] dark:hover:bg-white/[0.04]',
                colors.border,
                colors.text,
              )}
            >
              <CogIcon className="h-4 w-4 opacity-70" />
              Templates
            </Link>
          </>
        }
        filters={
          inbox !== 'mine' ? (
            <SegmentedControl
              options={statusFilterOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter((value || '') as '' | ApprovalStatus)}
              size="sm"
            />
          ) : null
        }
        hasFilters={hasFilters}
        onClearFilters={clearFilters}
        isLoading={isLoading}
        loadingLabel="Loading approvals…"
        isEmpty={data.length === 0}
        emptyState={{
          icon:
            inbox === 'mine' ? (
              <CheckCircleIcon className="h-12 w-12" />
            ) : (
              <DocumentTextIcon className="h-12 w-12" />
            ),
          title: hasFilters ? 'No matching approvals' : activeTab.emptyTitle,
          hint: hasFilters
            ? 'Try adjusting filters or clearing the search.'
            : activeTab.emptyHint,
          action: (
            <Link
              to="/approvals/templates"
              state={returnState}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
                colors.border,
                colors.text,
              )}
            >
              <CogIcon className="h-4 w-4 opacity-70" />
              Manage templates
            </Link>
          ),
        }}
      >
        {showGrouped ? (
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {grouped.map((group) => (
              <div key={group.status}>
                <div
                  className={cn(
                    'flex items-center gap-2 border-b px-5 py-2.5',
                    colors.border,
                    'bg-slate-50/80 dark:bg-slate-900/40',
                  )}
                >
                  <Badge variant={statusVariant[group.status]}>{statusLabel[group.status]}</Badge>
                  <span className={cn('text-xs tabular-nums', colors.textMuted)}>
                    {group.items.length}
                  </span>
                </div>
                <table className="w-full table-fixed text-sm">
                  <thead className="sr-only">{tableHeader}</thead>
                  <tbody>{renderRows(group.items)}</tbody>
                </table>
              </div>
            ))}
          </div>
        ) : (
          <table className="w-full table-fixed text-sm">
            <thead>{tableHeader}</thead>
            <tbody>{renderRows(data)}</tbody>
          </table>
        )}
      </GeneralList>

      <ConfirmationDialog
        isOpen={!!pendingDelete}
        onClose={() => {
          if (!deleting) {
            setPendingDelete(null);
            setDeleteError(null);
          }
        }}
        onConfirm={async () => {
          if (!pendingDelete) return;
          setDeleting(true);
          setDeleteError(null);
          try {
            await deleteApproval(pendingDelete.id);
            setPendingDelete(null);
            await mutate();
          } catch (err: any) {
            setDeleteError(
              err?.response?.data?.message || err?.message || 'Failed to delete approval',
            );
          } finally {
            setDeleting(false);
          }
        }}
        title="Delete approval"
        message={
          pendingDelete
            ? pendingDelete.status === 'DRAFT'
              ? `Delete draft “${pendingDelete.title}”? This cannot be undone.`
              : `Delete “${pendingDelete.title}” (${statusLabel[pendingDelete.status]})? This cannot be undone.`
            : 'Delete this approval?'
        }
        warningMessage={deleteError}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={deleting}
      />
    </>
  );
};

const ApprovalRow: React.FC<{
  approval: Approval;
  colors: ReturnType<typeof useTheme>['colors'];
  canDelete: boolean;
  returnState?: { from: string };
  onOpen: () => void;
  onDelete: () => void;
}> = ({ approval, colors, canDelete, returnState, onOpen, onDelete }) => {
  const waiting = approval.inboxRole === 'waiting';
  const analytics = approvalAnalytics(approval);
  const waitingHint =
    waiting && approval.myStage != null && approval.blockedByStage != null
      ? `Waiting for stage ${approval.blockedByStage} · You’re on stage ${approval.myStage}`
      : waiting && approval.myStage != null
        ? `You’re on stage ${approval.myStage} (waiting)`
        : null;

  const sourceHref = approvalSourcePath(approval.sourceModule, approval.sourceEntityId, {
    sourceEntityType: approval.sourceEntityType,
    payload: approval.payload,
  });
  const sourceLabel = approvalSourceLabel(approval.sourceModule);
  const statusWhen = formatDateTime(analytics.statusAt);
  const submittedWhen = formatDateTime(analytics.submittedAt);
  const lastWhen = formatDateTime(analytics.lastActedAt);

  return (
    <tr
      onClick={onOpen}
      className={cn(
        'group cursor-pointer border-b transition last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
        colors.border,
        waiting && 'bg-slate-50/40 dark:bg-slate-900/20',
      )}
    >
      <td className="px-3 py-3.5">
        <div className="flex items-center flex-nowrap">
          <Link
            onClick={(e) => e.stopPropagation()}
            to={`/approvals/${approval.id}`}
            state={returnState}
            title="Open"
            aria-label="Open approval"
            className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <EyeIcon className="w-5 h-5" />
          </Link>
          {canDelete ? (
            <button
              type="button"
              title="Delete"
              aria-label="Delete approval"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          ) : null}
        </div>
      </td>
      <td className="max-w-0 px-5 py-3.5">
        <div className="min-w-0">
          <p className={cn('truncate font-medium', waiting ? colors.textMuted : colors.text)}>
            {approval.title}
          </p>
          {waitingHint ? (
            <p className="mt-0.5 truncate text-xs font-medium text-amber-700 dark:text-amber-300">
              {waitingHint}
            </p>
          ) : null}
          {approval.summary ? (
            <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>{approval.summary}</p>
          ) : null}
          <div className={cn('mt-1 space-y-0.5 text-xs sm:hidden', colors.textMuted)}>
            <p className="truncate">
              {sourceLabel}
              {analytics.submittedBy ? ` · ${analytics.submittedBy}` : ''}
            </p>
            {statusWhen ? (
              <p className="truncate tabular-nums">
                {statusAtCaption[approval.status]} {statusWhen}
              </p>
            ) : null}
            {analytics.lastComment ? (
              <p className="truncate italic">“{analytics.lastComment}”</p>
            ) : null}
          </div>
        </div>
      </td>
      <td className={cn('hidden px-3 py-3.5 sm:table-cell', colors.textMuted)}>
        {sourceHref ? (
          <Link
            onClick={(e) => e.stopPropagation()}
            to={sourceHref}
            className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
          >
            {sourceLabel}
          </Link>
        ) : (
          <span className={cn('font-medium', colors.text)}>{sourceLabel}</span>
        )}
        <span className="block text-xs">{approval.sourceEntityType}</span>
        {analytics.submittedBy ? (
          <span className="mt-0.5 block truncate text-xs">By {analytics.submittedBy}</span>
        ) : null}
      </td>
      <td className={cn('hidden truncate px-3 py-3.5 lg:table-cell', colors.text)}>
        {approval.approvalType?.name ?? `#${approval.approvalTypeId}`}
      </td>
      <td className="px-3 py-3.5">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant={statusVariant[approval.status]} className="whitespace-nowrap">
              {statusLabel[approval.status]}
            </Badge>
            {waiting ? (
              <Badge variant="info">Waiting</Badge>
            ) : approval.inboxRole === 'actionable' ? (
              <Badge variant="warning">Your turn</Badge>
            ) : null}
            {analytics.overdue ? <Badge variant="danger">Overdue</Badge> : null}
          </div>
          {statusWhen ? (
            <p className={cn('text-[11px] leading-snug tabular-nums', colors.textMuted)}>
              <span className="font-medium">{statusAtCaption[approval.status]}</span>
              <br />
              {statusWhen}
            </p>
          ) : null}
        </div>
      </td>
      <td className={cn('hidden whitespace-nowrap px-3 py-3.5 md:table-cell', colors.textMuted)}>
        {submittedWhen ? (
          <div>
            <p className="text-xs tabular-nums">{submittedWhen}</p>
            {analytics.submittedBy ? (
              <p className="mt-0.5 truncate text-[11px]">{analytics.submittedBy}</p>
            ) : null}
          </div>
        ) : (
          <span className="text-xs">—</span>
        )}
      </td>
      <td className={cn('hidden px-3 py-3.5 xl:table-cell', colors.textMuted)}>
        {analytics.progressTotal > 0 ? (
          <div className="space-y-0.5 text-xs">
            <p className={cn('font-medium tabular-nums', colors.text)}>
              {analytics.progressDone}/{analytics.progressTotal} steps
            </p>
            {analytics.currentStage != null ? (
              <p>
                Stage {analytics.currentStage}
                {analytics.stageCount > 1 ? ` / ${analytics.stageCount}` : ''}
              </p>
            ) : approval.status === 'APPROVED' || approval.status === 'REJECTED' ? (
              <p>Complete</p>
            ) : null}
            {analytics.pendingAssignees.length > 0 ? (
              <p className="truncate" title={analytics.pendingAssignees.join(', ')}>
                Waiting: {analytics.pendingAssignees.slice(0, 2).join(', ')}
                {analytics.pendingAssignees.length > 2
                  ? ` +${analytics.pendingAssignees.length - 2}`
                  : ''}
              </p>
            ) : null}
          </div>
        ) : (
          <span className="text-xs">—</span>
        )}
      </td>
      <td className={cn('hidden px-3 py-3.5 2xl:table-cell', colors.textMuted)}>
        {analytics.lastActedAt ? (
          <div className="min-w-0 space-y-0.5 text-xs">
            <p className="tabular-nums">{lastWhen}</p>
            {analytics.lastActor ? <p className="truncate">{analytics.lastActor}</p> : null}
            {analytics.lastComment ? (
              <p className="truncate italic" title={analytics.lastComment}>
                “{analytics.lastComment}”
              </p>
            ) : null}
          </div>
        ) : (
          <span className="text-xs">—</span>
        )}
      </td>
      <td className={cn('hidden truncate px-3 py-3.5 xl:table-cell', colors.textMuted)}>
        {departmentName(approval) || '—'}
      </td>
    </tr>
  );
};
